const CANONICAL_CURVE_25519 = "CURVE_25519";
const CANONICAL_P_384 = "P-384";

/**
 * Maps an API/config curve identifier to its canonical internal value, or null when the
 * identifier is absent or unrecognized.
 *
 * @param {String|undefined} eccCurve
 * @returns {"CURVE_25519"|"P-384"|null}
 */
function resolveEccCurve(eccCurve) {
  if (!eccCurve) {
    return null;
  }
  const normalized = String(eccCurve).toUpperCase().replace(/-/g, "_");
  if (normalized === "CURVE_25519" || normalized === "CURVE25519") {
    return CANONICAL_CURVE_25519;
  }
  if (
    normalized === "P_384" ||
    normalized === "P384" ||
    normalized === "CURVE_384" ||
    normalized === "CURVE384"
  ) {
    return CANONICAL_P_384;
  }
  return null;
}

/**
 * Normalizes ECC curve names from API/config into canonical internal values.
 * Accepts hyphen or underscore forms (e.g. CURVE-25519, CURVE_25519, Curve25519).
 *
 * Fails closed: an absent or unrecognized curve is an error rather than a silent P-384
 * default, because defaulting would perform key agreement on the wrong curve.
 *
 * @param {String} eccCurve
 * @returns {"CURVE_25519"|"P-384"}
 */
export function normalizeEccCurve(eccCurve) {
  const resolved = resolveEccCurve(eccCurve);
  if (!resolved) {
    if (!eccCurve) {
      throw new Error("Encryption curve is required.");
    }
    throw new Error(`Unsupported encryption curve: ${eccCurve}`);
  }
  return resolved;
}

/**
 * Resolves the encryption curve. Throws when unset or unsupported.
 *
 * @param {String|undefined} encryptionCurve
 * @returns {"CURVE_25519"|"P-384"}
 */
export function requireEncryptionCurve(encryptionCurve) {
  return normalizeEccCurve(encryptionCurve);
}

/**
 * Resolves the signing curve. Throws when unset or unsupported.
 *
 * @param {String|undefined} signingCurve
 * @returns {"CURVE_25519"|"P-384"}
 */
export function requireSigningCurve(signingCurve) {
  const resolved = resolveEccCurve(signingCurve);
  if (!resolved) {
    if (!signingCurve) {
      throw new Error("Signing curve is required.");
    }
    throw new Error(`Unsupported signing curve: ${signingCurve}`);
  }
  return resolved;
}

/**
 * @param {String} eccCurve
 * @returns {boolean}
 */
export function isCurve25519(eccCurve) {
  return normalizeEccCurve(eccCurve) === CANONICAL_CURVE_25519;
}

/**
 * Returns Web Crypto subtle API when available (browser or Node 19+).
 */
export function getWebCryptoSubtle() {
  if (typeof globalThis !== "undefined" && globalThis.crypto?.subtle) {
    return globalThis.crypto.subtle;
  }
  if (typeof window !== "undefined" && window.crypto?.subtle) {
    return window.crypto.subtle;
  }
  throw new Error("Web Crypto API is not available");
}
